import type { CreditCardSource } from './generated/enums.js';

/**
 * Card providers whose faturas are ingested natively from the provider API as
 * first-class CardStatements (no bank-issued invoice document, no institution link).
 *
 * Membership in this family is load-bearing in three places that must never diverge:
 * bill intake (a native provider is mutually exclusive with cardIssuerInstitutionId
 * and cardAllocations), settlement, and GL grouping (one liability bucket per
 * provider per entity). Add new native providers here only — consumers branch on
 * {@link isFaturaNativeCardProvider} / {@link FaturaNativeCardProvider} and the
 * per-provider switches they contain fail the type-check until extended.
 */
export const FATURA_NATIVE_CARD_PROVIDERS = [
  'CLARA',
  'CONTA_SIMPLES',
  'BREX',
] as const satisfies readonly CreditCardSource[];

export type FaturaNativeCardProvider = (typeof FATURA_NATIVE_CARD_PROVIDERS)[number];

export function isFaturaNativeCardProvider(
  value: string | null | undefined,
): value is FaturaNativeCardProvider {
  return value != null && (FATURA_NATIVE_CARD_PROVIDERS as readonly string[]).includes(value);
}

/**
 * Credit card default values.
 * Centralized to avoid divergent rules across api, worker, services.
 *
 * - OPEN_FINANCE: Used when creating cards from Pluggy/Open Finance and creditData
 *   (balanceCloseDate, balanceDueDate) is missing. Common industry fallback (1st, 10th).
 * - DRAFT: Used for manually created draft cards (import, document assignment).
 *   Conservative fallback for unknown billing cycles (1st, 5th).
 */
export const CREDIT_CARD_DEFAULTS = {
  /** Open Finance / Pluggy: fallback when creditData has no dates */
  OPEN_FINANCE: {
    closingDay: 1,
    dueDay: 10,
  },
  /** Draft/manual cards: import, document assignment, etc. */
  DRAFT: {
    closingDay: 1,
    dueDay: 5,
  },
} as const;
